import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MY_PATH_URL } from '../../features/paths/utils/pathsChatUrl';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Login from '../../features/auth/components/Login';
import { appsAPI } from '../../features/apps/api/appsAPI';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { navigateAppLaunch, resolveAppLaunchTarget, isStandaloneReasoningApp } from '../../features/apps/utils/appDisplayUtils';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.search]);
  
  const handleBack = () => {
    const from = location.state?.from?.pathname || '/';
    navigate(from);
  };

  const handleLoginSuccess = async (signedInUser) => {
    const account = signedInUser || user;
    if (account?.must_change_password) {
      navigate('/set-password', { replace: true });
      return;
    }
    await navigateAfterPasswordLogin(navigate, location.state, account);
  };

  return (
    <ProtectedRoute requiresAuth={false}>
      <Login onBack={handleBack} onLoginSuccess={handleLoginSuccess} locationState={location.state} />
    </ProtectedRoute>
  );
};

export async function navigateAfterPasswordLogin(navigate, locationState, signedInUser) {
    const returnTo = locationState?.returnTo;
    if (returnTo) {
      navigateAppLaunch(navigate, returnTo);
      return;
    }

    const intendedAppId = locationState?.intendedAppId;
    const intendedPurchase = locationState?.intendedPurchase;
    const intendedRoute = locationState?.intendedRoute || 'experience'; // 'tests' | 'experience' | 'tool'
    const intendedToolType = locationState?.intendedToolType || 'cv';
    
    if (intendedAppId) {
      // If user intended to purchase the app, buy it now (before navigating)
      if (intendedPurchase) {
        try {
          await appsAPI.buyApp(intendedAppId);
          // Trigger event to refresh myApps list in AppStoreView
          window.dispatchEvent(new CustomEvent('appPurchased', { detail: { appId: intendedAppId } }));
        } catch (err) {
          console.error('Could not purchase app:', err);
          // Still navigate even if purchase fails - user can try again from detail page
        }
      }
      
      // Record app opened usage (non-blocking)
      appsAPI.recordUsage(intendedAppId, 'app_opened').catch((err) => {
        console.warn('Could not record app usage:', err);
      });

      // Standalone reasoning apps → /website-reasoning-test?type= (never old experience suite)
      try {
        const appData = await appsAPI.getAppById(intendedAppId);
        if (isStandaloneReasoningApp(appData)) {
          const reasoningTarget = resolveAppLaunchTarget(appData);
          if (reasoningTarget) {
            navigateAppLaunch(navigate, reasoningTarget);
            return;
          }
        }
      } catch (err) {
        console.warn('Could not resolve reasoning launch after login:', err);
      }
      
      // Tool apps: My Path -> the My Path folder in /chat; CV/Cover Letter -> application builder; Flash Cards -> flash-cards page; Code Lab -> code lab IDE
      if (intendedRoute === 'tool') {
        if (intendedToolType === 'my_path') {
          navigate(MY_PATH_URL, { replace: true });
        } else if (intendedToolType === 'uai' || (intendedToolType || '').trim().toLowerCase() === 'uai') {
          navigate('/chat', { replace: true });
        } else if (intendedToolType === 'flash_cards' || (intendedToolType || '').trim().toLowerCase() === 'flash cards') {
          navigate(`/apps-store/${intendedAppId}/flash-cards`, { replace: true });
        } else if (intendedToolType === 'caius_explore' || (intendedToolType || '').trim().toLowerCase() === 'caius_explore') {
          navigate(`/apps-store/${intendedAppId}/caius-explore`, { replace: true });
        } else if (intendedToolType === 'notes' || (intendedToolType || '').trim().toLowerCase() === 'notes') {
          navigate('/notes', { replace: true });
        } else if (intendedToolType === 'library' || (intendedToolType || '').trim().toLowerCase() === 'library' || (intendedToolType || '').trim().toLowerCase() === 'smart library') {
          navigate(`/apps-store/${intendedAppId}/library`, { replace: true });
        } else if (
          intendedToolType === 'supercurricular_books' ||
          (intendedToolType || '').trim().toLowerCase() === 'supercurricular books'
        ) {
          navigate(`/apps-store/${intendedAppId}/supercurricular-books`, { replace: true });
        } else if (
          intendedToolType === 'code_lab' ||
          (intendedToolType || '').trim().toLowerCase() === 'code lab' ||
          (intendedToolType || '').trim().toLowerCase() === 'code labs'
        ) {
          navigate(`/apps-store/${intendedAppId}/code-lab`, { replace: true });
        } else if (
          intendedToolType === 'financial_modeling_lab' ||
          (intendedToolType || '').trim().toLowerCase() === 'financial modeling lab' ||
          (intendedToolType || '').trim().toLowerCase() === 'financial modelling lab'
        ) {
          navigate('/financial-modeling-lab', { replace: true });
        } else if (
          intendedToolType === 'cv' ||
          intendedToolType === 'cover_letter' ||
          intendedToolType === 'personal_statement'
        ) {
          navigate(`/application-builder/form/${intendedToolType}`, { replace: true });
        } else if (
          intendedToolType === 'medicine_mmi_interview' ||
          intendedToolType === 'medicine_panel_interview' ||
          intendedToolType === 'oxbridge_interview' ||
          intendedToolType === 'interview_simulator' ||
          intendedToolType === 'investment_banking_interview' ||
          intendedToolType === 'private_equity_interview' ||
          intendedToolType === 'engineering_interview' ||
          intendedToolType === 'computer_science_interview' ||
          intendedToolType === 'law_interview' ||
          intendedToolType === 'operations_fs_interview' ||
          intendedToolType === 'scholarship_interview'
        ) {
          navigate(`/website-interview?toolType=${encodeURIComponent(intendedToolType)}`, { replace: true });
        } else {
          // Quiz / practice apps (e.g. Medical Interviews) and other tools
          const launchTarget = resolveAppLaunchTarget({ exam_name: intendedToolType, title: intendedToolType });
          if (launchTarget) {
            navigateAppLaunch(navigate, launchTarget);
          } else {
            navigate('/chat', { replace: true });
          }
        }
        return;
      }
      // Navigate based on intended route (tests for productivity, experience for others)
      const route = intendedRoute === 'tests' ? 'tests' : 'experience';
      navigate(`/apps-store/${intendedAppId}/${route}`, { replace: true });
      return;
    }
    const isCertAdmin = signedInUser?.role === 'certadmin';
    // /cert-portal for certadmin, /chat for everyone else. Instructors land on
    // /chat like any other account and reach their dashboard from the profile
    // menu; they are not routed away from the student experience.
    const defaultPath = isCertAdmin ? '/cert-portal' : '/chat';
    let from = locationState?.from?.pathname || defaultPath;
    if (from === '/' || from === '/login' || from === '/dashboard' || from === '/my-apps' || from === '/my-apps-and-workspace') {
      from = defaultPath;
    }
    navigate(from, { replace: true });
}

export default LoginPage;